/**
 * State for the snapshot editor.
 *
 * Held as a hook in home.js and passed down as one `editor` prop, the way the
 * rest of this page passes its state around. (A context provider would work too,
 * but wrapping home's JSX in one indents the entire tree for no gain.)
 *
 * What it holds:
 *   - the snapshot summaries the tree renders
 *   - the selected snapshot's full config, plus the `contentHash` it was read
 *     with. The agent writes to the same folder, so that hash is what a save
 *     quotes back to prove it is not overwriting someone else's work.
 *
 * Passing `baseID: null` makes the hook inert — nothing is fetched — so home.js
 * can call it unconditionally on every base.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import * as snapshotApi from "../modules/snapshotEditorApi";
import { SNAPSHOT_IFRAME_ID } from "../modules/snapshotEditorApi";
import { sendCommand } from "../modules/playEngineBridge";
import { diffSnapshot, mergePatches } from "../modules/snapshotPatch";
import { downloadSnapshotJson, downloadSnapshotFolderArchive } from "../modules/snapshotFileTransfer";
import { isInsideFolder, normalizeFolderPath } from "../modules/snapshotTree";
import useSnapshotJobs from "./useSnapshotJobs";

const SELECTED_SNAPSHOT_STORAGE_KEY = "snapshot.selectedSnapshot";

// Writes go straight to a file the agent may also be writing, so they are
// coalesced harder than the version editor's 200ms: a burst of typing should
// land as one write, not fifteen.
const SAVE_DEBOUNCE_MS = 600;

// ── Undo history ────────────────────────────────────────────────────────────
// Deliberately in memory only: this is "take that back", not a revision log.
// It dies with the tab, and switching files starts a fresh one.
const HISTORY_LIMIT = 30;

// Successive edits to the SAME field inside this window become one step.
// Dragging a slider or typing a word fires a change per pixel and per key —
// each one its own undo step would make undo useless.
const HISTORY_COALESCE_MS = 700;

// Marks a key that did not exist before the change, so undoing removes it again
// rather than writing `undefined` into the snapshot.
const ABSENT = Symbol("absent");

/** Snapshot the given top-level keys, remembering which ones were absent. */
function captureKeys(source, keys) {
  const state = {};
  for (const key of keys) {
    state[key] = _.has(source, [key]) ? _.cloneDeep(_.get(source, [key])) : ABSENT;
  }
  return state;
}

/** Put a captured state back onto a snapshot clone. */
function restoreKeys(target, state) {
  for (const [key, value] of Object.entries(state)) {
    if (value === ABSENT) delete target[key];
    else target[key] = _.cloneDeep(value);
  }
  return target;
}

/**
 * Push restored config values into the running preview.
 *
 * The same command the settings panel sends when you edit a field
 * (`previewConfigChange` → dispatch-action / updateConfig), so an undone value
 * lands in the playable exactly the way a hand-made one does. Rebooting the
 * iframe would also work but costs a full engine boot and loses the playable's
 * runtime state — and the engine takes only one snapshot per boot, so there is
 * no cheaper "just load this again".
 *
 * Component config ONLY, deliberately: assets, parenting and localisation each
 * have their own commands and their own ordering rules. Undo still restores
 * those in the snapshot; they just need a preview reload to be seen.
 */
async function applyConfigToPreview(before, after) {
  const previousById = _.keyBy(_.get(before, "components") || [], "id");

  for (const component of _.get(after, "components") || []) {
    const previous = previousById[component.id];
    if (!previous) continue;

    const config = component.config || {};
    const previousConfig = previous.config || {};
    const partial = {};
    for (const key of _.union(Object.keys(config), Object.keys(previousConfig))) {
      if (!_.isEqual(config[key], previousConfig[key])) partial[key] = config[key];
    }
    if (_.isEmpty(partial)) continue;

    try {
      await sendCommand(SNAPSHOT_IFRAME_ID, {
        type: "playengine:dispatch-action",
        action: { target: component.id, name: "updateConfig", args: partial },
      });
    } catch (err) {
      // The snapshot is already correct; only the live view missed it.
      console.warn(`[snapshotEditor]  live preview update failed for ${component.id}: ${err.message}`);
    }
  }
}

export default function useSnapshotEditor({
  baseID,
  flow = null,
  taskDelivery = false,
  exportAsVersion = false,
  createSnackbar,
}) {
  const [snapshots, setSnapshots] = useState([]);
  const [areSnapshotsLoaded, setAreSnapshotsLoaded] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState(null);

  // The full config of the selected snapshot as last read from (or written to)
  // disk, the file it came from, and the `contentHash` of the bytes it was read
  // with — one state object, because a render in which they disagree is a bug
  // rather than a stage of loading. `fileName` trails `selectedFileName` during
  // a switch, and everything that talks to the engine keys off `fileName`: the
  // preview must not boot on a snapshot that is about to be replaced, since the
  // engine accepts exactly one snapshot per boot and drops a late second one.
  const [loaded, setLoaded] = useState({
    fileName: null,
    snapshot: null,
    contentHash: null,
    sizeBytes: null,
    estimatedBytes: null,
  });
  const [isSnapshotLoading, setIsSnapshotLoading] = useState(false);

  // Orientation is shared: the preview sizes the iframe by it and the settings
  // panel labels its position editors with it, so they must not disagree.
  const [isPortrait, setIsPortrait] = useState(true);

  // Clicking quickly through the tree can land responses out of order; only the
  // newest request may install its result.
  const loadSequenceRef = useRef(0);

  // Save pipeline. `snapshotRef` is the authoritative in-flight config: the
  // panels call back synchronously in bursts and would otherwise each diff
  // against the same stale render. `contentHashRef` follows every save so a
  // second save in the same burst quotes the hash the first one produced.
  const snapshotRef = useRef(null);
  // The content the server had when the queued edits were made. A 409 needs it to
  // tell "the agent changed this key too" from "the agent changed a different key",
  // which is the difference between dropping the user's edit and replaying it.
  const serverSnapshotRef = useRef(null);
  const contentHashRef = useRef(null);
  const pendingRef = useRef({ patches: [], removedPaths: [] });
  const saveTimerRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [conflict, setConflict] = useState(null);

  // Undo history. Entries hold only the top-level keys a change touched, before
  // and after — `diffSnapshot` works at that granularity anyway, and keeping
  // whole snapshots would mean 30 copies of the inline base64 assets.
  // `isTimeTravelRef` keeps undo/redo from recording themselves as new edits.
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const isTimeTravelRef = useRef(false);
  const [historyDepth, setHistoryDepth] = useState({ undo: 0, redo: 0 });

  // Bumped by undo/redo. The settings panels do not read their fields off the
  // snapshot: they hold their own copy, taken from the engine when the snapshot
  // opened and updated as the user edits. A restored value therefore has to be
  // announced — a new `snapshot` object alone tells them nothing, and the panel
  // would keep showing the value that was just undone. See
  // `externalConfigRevision` in containerSnapshotConfig.
  const [configRevision, setConfigRevision] = useState(0);

  const syncHistoryDepth = useCallback(() => {
    setHistoryDepth({ undo: undoStackRef.current.length, redo: redoStackRef.current.length });
  }, []);

  const clearHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    syncHistoryDepth();
  }, [syncHistoryDepth]);

  const notify = useCallback(
    (message, variant) => {
      if (createSnackbar) createSnackbar(message, variant, false);
      else console.warn(`[snapshotEditor] ${message}`);
    },
    [createSnackbar]
  );

  const refreshSnapshots = useCallback(async () => {
    if (!baseID) return [];

    const result = await snapshotApi.listSnapshots(baseID);
    if (!result.ok) {
      notify(`Could not list snapshots: ${result.error}`, "error");
      setAreSnapshotsLoaded(true);
      return [];
    }

    const list = _.get(result, "data.snapshots", []);
    setSnapshots(list);
    setAreSnapshotsLoaded(true);
    return list;
  }, [baseID, notify]);

  const loadSnapshot = useCallback(
    async (fileName) => {
      if (!baseID || !fileName) return;

      const sequence = ++loadSequenceRef.current;
      setIsSnapshotLoading(true);

      // Let go of the previous file before the request, so nothing downstream
      // spends the wait rendering one file's config under another file's name.
      snapshotRef.current = null;
      serverSnapshotRef.current = null;
      contentHashRef.current = null;
      pendingRef.current = { patches: [], removedPaths: [] };
      // History belongs to the file it was made against — including on a
      // re-read of the same file, where the disk may have moved underneath it.
      clearHistory();
      setLoaded({ fileName: null, snapshot: null, contentHash: null, sizeBytes: null, estimatedBytes: null });

      const result = await snapshotApi.getSnapshot(baseID, fileName);
      if (sequence !== loadSequenceRef.current) return; // superseded

      setIsSnapshotLoading(false);
      if (!result.ok) {
        notify(`Could not open "${fileName}": ${result.error}`, "error");
        return;
      }

      const loadedSnapshot = _.get(result, "data.snapshot", null);
      const loadedHash = _.get(result, "data.contentHash", null);
      snapshotRef.current = loadedSnapshot;
      serverSnapshotRef.current = loadedSnapshot;
      contentHashRef.current = loadedHash;
      setLoaded({
        fileName,
        snapshot: loadedSnapshot,
        contentHash: loadedHash,
        sizeBytes: _.get(result, "data.sizeBytes", null),
        estimatedBytes: _.get(result, "data.estimatedBytes", null),
      });
    },
    [baseID, notify, clearHistory]
  );

  /**
   * Send everything queued. Returns once the request settles, so callers that
   * must not race a save (switching files, exporting) can await it.
   */
  const flushSave = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    const pending = pendingRef.current;
    if (!pending.patches.length && !pending.removedPaths.length) return;
    if (!baseID || !selectedFileName || !contentHashRef.current) return;

    pendingRef.current = { patches: [], removedPaths: [] };
    const sentSnapshot = snapshotRef.current;
    setIsSaving(true);

    let result = await snapshotApi.saveSnapshot(
      baseID,
      selectedFileName,
      contentHashRef.current,
      pending.patches,
      pending.removedPaths
    );

    // Someone else — almost always the agent — wrote the file while it was open
    // here. Reloading is right; throwing the user's edit away with it is not.
    // Patches are top-level keys, so "did the other writer touch THIS key" is
    // answerable: compare the fresh file against what the server had when the
    // edit was made. Keys they left alone are replayed onto the fresh content;
    // only a genuine same-key collision is dropped, and it is named.
    if (result.status === 409) {
      const current = _.get(result, "error.snapshot", null);
      const currentHash = _.get(result, "error.contentHash", null);
      const before = serverSnapshotRef.current;

      const untouched = (path) =>
        Boolean(before) && Boolean(current) && _.isEqual(_.get(before, path), _.get(current, path));

      const replayPatches = pending.patches.filter((patch) => untouched(patch.path));
      const replayRemovals = pending.removedPaths.filter(untouched);
      const collisions = _.union(
        pending.patches.filter((patch) => !untouched(patch.path)).map((patch) => patch.path),
        pending.removedPaths.filter((path) => !untouched(path))
      );

      if (current) {
        const merged = _.cloneDeep(current);
        for (const patch of replayPatches) _.set(merged, patch.path, patch.value);
        for (const path of replayRemovals) _.unset(merged, path);

        snapshotRef.current = merged;
        serverSnapshotRef.current = current;
        contentHashRef.current = currentHash;
        setLoaded((previous) => ({ ...previous, snapshot: merged, contentHash: currentHash }));
      }

      // One retry, against the hash the server just handed back. Not a loop: a
      // second conflict means writes are landing faster than a round trip, and
      // the honest answer there is to keep the edit queued and say so.
      if (current && (replayPatches.length || replayRemovals.length)) {
        result = await snapshotApi.saveSnapshot(
          baseID,
          selectedFileName,
          contentHashRef.current,
          replayPatches,
          replayRemovals
        );
        if (!result.ok) {
          pendingRef.current = {
            patches: mergePatches(pendingRef.current.patches, replayPatches),
            removedPaths: _.union(pendingRef.current.removedPaths, replayRemovals),
          };
        }
      }

      setConflict({ fileName: selectedFileName, at: new Date() });
      notify(
        collisions.length
          ? `"${selectedFileName}" changed on disk while you were editing it. Your edit was kept, ` +
            `except ${collisions.join(", ")} — that part was changed on disk too and was reloaded.`
          : `"${selectedFileName}" changed on disk while you were editing it — reloaded, and your edit was kept.`,
        "warning"
      );
    }

    setIsSaving(false);

    if (result.ok) {
      contentHashRef.current = _.get(result, "data.contentHash", null);
      serverSnapshotRef.current = snapshotRef.current || sentSnapshot;
      const savedBytes = _.get(result, "data.sizeBytes", null);
      setLoaded((previous) => ({
        ...previous,
        contentHash: contentHashRef.current,
        sizeBytes: savedBytes == null ? previous.sizeBytes : savedBytes,
        estimatedBytes: _.get(result, "data.estimatedBytes", previous.estimatedBytes),
      }));

      // The tree's entry for this file is now out of date in two ways, and
      // both show as a size: it carries the old byte count, and it carries a
      // build measurement that described bytes which no longer exist. The
      // listing is the whole library and saves are frequent, so it is patched
      // here rather than re-read — dropping `builds` is not a guess, a file
      // that changed has not been built since.
      setSnapshots((previous) =>
        previous.map((item) =>
          item.fileName === selectedFileName
            ? { ..._.omit(item, "builds"), ...(savedBytes == null ? {} : { sizeBytes: savedBytes }) }
            : item
        )
      );
      return;
    }
    if (result.status === 409) return; // already reported above

    notify(`Could not save "${selectedFileName}": ${result.error}`, "error");
  }, [baseID, notify, selectedFileName]);

  /**
   * Take an edited copy of the whole config — the shape the settings panels hand
   * back — and queue only what actually changed. See modules/snapshotPatch.
   */
  const applySnapshotChange = useCallback(
    (nextSnapshot, { coalesceKey = null } = {}) => {
      const previous = snapshotRef.current;
      if (!nextSnapshot || !previous) return;

      // A locked snapshot is frozen. The panels render read-only so this should
      // not be reachable, and the server refuses the write regardless — this
      // stops a stray edit from moving the on-screen config out of step with
      // the file it can no longer be saved to.
      if (_.get(previous, "metadata.locked", false) === true) return;

      const { patches, removedPaths } = diffSnapshot(previous, nextSnapshot);
      if (!patches.length && !removedPaths.length) return;

      // Record before the change lands. Undo/redo call back through here to
      // apply their own state, and those must not become history themselves.
      if (!isTimeTravelRef.current) {
        const keys = _.union(patches.map((patch) => patch.path), removedPaths);
        const previousEntry = undoStackRef.current[undoStackRef.current.length - 1];
        const canCoalesce =
          coalesceKey &&
          previousEntry &&
          previousEntry.coalesceKey === coalesceKey &&
          Date.now() - previousEntry.at < HISTORY_COALESCE_MS &&
          // Same keys too, or the step's `before` and `after` would describe
          // different parts of the snapshot and undo would restore only half of
          // it. For one field this holds anyway; the check is what makes it safe
          // to rely on rather than a coincidence.
          _.isEqual(Object.keys(previousEntry.before).sort(), [...keys].sort());

        if (canCoalesce) {
          // Same field, still typing/dragging: extend the existing step rather
          // than adding one. `before` stays as it was when the burst started.
          previousEntry.after = captureKeys(nextSnapshot, keys);
          previousEntry.at = Date.now();
        } else {
          undoStackRef.current.push({
            coalesceKey,
            at: Date.now(),
            before: captureKeys(previous, keys),
            after: captureKeys(nextSnapshot, keys),
          });
          if (undoStackRef.current.length > HISTORY_LIMIT) undoStackRef.current.shift();
        }

        // A fresh edit abandons the branch the user had redone away from.
        redoStackRef.current = [];
        syncHistoryDepth();
      }

      snapshotRef.current = nextSnapshot;
      setLoaded((previous) => ({ ...previous, snapshot: nextSnapshot }));

      pendingRef.current = {
        patches: mergePatches(pendingRef.current.patches, patches),
        removedPaths: _.union(pendingRef.current.removedPaths, removedPaths),
      };

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        void flushSave();
      }, SAVE_DEBOUNCE_MS);
    },
    [flushSave, syncHistoryDepth]
  );

  /**
   * Set one path inside the snapshot, cloning so React sees a new object.
   *
   * The path doubles as the history's coalesce key: repeated writes to the same
   * field are one undo step, and moving to a different field starts a new one.
   * `diffSnapshot` only reports top-level keys, so the history could not tell
   * two fields apart on its own.
   *
   * Array indices are dropped from that key. Editing a field with several
   * components selected writes one path per component, and the panel interleaves
   * them (`components.2.…`, `components.7.…`, again, again) as the drag goes on —
   * with the index in the key no two consecutive writes ever match, so a single
   * drag would fill the history with steps and push its own starting point out
   * of it. The entries hold the whole `components` key either way, so folding
   * them together restores every component the gesture touched.
   */
  const setSnapshotPathValue = useCallback(
    (path, value) => {
      if (!snapshotRef.current) return;
      const next = _.cloneDeep(snapshotRef.current);
      _.set(next, path, value);
      const coalesceKey = _.toPath(path)
        .map((segment) => (/^\d+$/.test(segment) ? "*" : segment))
        .join(".");
      applySnapshotChange(next, { coalesceKey });
    },
    [applySnapshotChange]
  );

  /**
   * Step back and forward through recent edits.
   *
   * Both replay through `applySnapshotChange`, so a restored value saves and
   * reaches the preview exactly like a hand-made edit — the only difference is
   * that the flag stops the replay from being recorded again.
   */
  const timeTravel = useCallback(
    (fromStack, toStack, side) => {
      const entry = fromStack.current.pop();
      if (!entry || !snapshotRef.current) return;

      const previous = snapshotRef.current;
      const next = restoreKeys(_.cloneDeep(previous), side === "undo" ? entry.before : entry.after);
      toStack.current.push(entry);

      isTimeTravelRef.current = true;
      try {
        applySnapshotChange(next);
      } finally {
        isTimeTravelRef.current = false;
      }
      syncHistoryDepth();
      // Tell the settings panels their copy of the config is out of date.
      setConfigRevision((revision) => revision + 1);

      // Hand the restored values to the live playable rather than rebooting it.
      // Fire-and-forget: the snapshot is already updated and saved either way.
      void applyConfigToPreview(previous, next);
    },
    [applySnapshotChange, syncHistoryDepth]
  );

  const undo = useCallback(
    () => timeTravel(undoStackRef, redoStackRef, "undo"),
    [timeTravel]
  );

  const redo = useCallback(
    () => timeTravel(redoStackRef, undoStackRef, "redo"),
    [timeTravel]
  );

  /**
   * Run a file-level operation (create, rename, duplicate, discard, move) and
   * refresh the tree. Pending edits are flushed first: renaming a file out from
   * under a queued save would make that save target a path that no longer exists.
   */
  const mutateSnapshot = useCallback(
    async (operation, payload) => {
      if (!baseID) return null;
      await flushSave();

      const result = await snapshotApi.mutateSnapshot(baseID, operation, payload);
      if (!result.ok) {
        notify(`Could not ${operation}: ${result.error}`, "error");
        return null;
      }

      await refreshSnapshots();

      // Locking rewrites the open file's metadata, so re-read it — otherwise
      // the panels would stay editable until something else reloaded them.
      if (
        (operation === "setLock" && payload.fileName === selectedFileName) ||
        (operation === "setFolderLock" &&
          (_.get(result, "data.affected") || []).includes(selectedFileName))
      ) {
        await loadSnapshot(selectedFileName);
        return result.data;
      }

      // Renaming, duplicating or adding produces a file the user expects to be
      // looking at; discarding the open one leaves nothing to look at.
      const produced = _.get(result, "data.fileName");
      if (operation === "rename" && payload.fileName === selectedFileName && produced) {
        setSelectedFileName(produced);
        localStorage.setItem(SELECTED_SNAPSHOT_STORAGE_KEY, produced);
        void loadSnapshot(produced);
      } else if ((operation === "duplicate" || operation === "create") && produced) {
        setSelectedFileName(produced);
        localStorage.setItem(SELECTED_SNAPSHOT_STORAGE_KEY, produced);
        void loadSnapshot(produced);
      } else if (operation === "discard" && payload.fileName === selectedFileName) {
        setSelectedFileName(null);
        setLoaded({ fileName: null, snapshot: null, contentHash: null });
        snapshotRef.current = null;
        contentHashRef.current = null;
      }

      return result.data;
    },
    [baseID, flushSave, loadSnapshot, notify, refreshSnapshots, selectedFileName]
  );

  /**
   * Hand one snapshot to the browser as a `.json` file.
   *
   * The bytes come from disk rather than from the in-memory copy, so a file the
   * user has not opened downloads without loading it — and so a download is
   * always the file the agent and the export step see. Queued edits to the open
   * snapshot are flushed first, or the download would quietly be the older one.
   */
  const downloadSnapshot = useCallback(
    async (fileName) => {
      if (!baseID || !fileName) return false;
      await flushSave();

      const result = await snapshotApi.getSnapshot(baseID, fileName);
      if (!result.ok) {
        notify(`Could not download ${fileName}: ${result.error}`, "error");
        return false;
      }

      downloadSnapshotJson(result.data.snapshot, fileName);
      return true;
    },
    [baseID, flushSave, notify]
  );

  const downloadFolder = useCallback(async (folderPath) => {
    if (!baseID) return false;
    const selectedPath = normalizeFolderPath(folderPath);
    const files = snapshots.filter((item) =>
      isInsideFolder(normalizeFolderPath(item.exportFolder), selectedPath)
    ).sort((a, b) => a.fileName.localeCompare(b.fileName));
    if (!selectedPath || files.length === 0) {
      notify("This folder has no snapshots to download.", "error");
      return false;
    }

    try {
      await flushSave();
      const downloaded = [];
      for (const item of files) {
        const result = await snapshotApi.getSnapshot(baseID, item.fileName);
        if (!result.ok) throw new Error(`Could not read ${item.fileName}: ${result.error}`);
        downloaded.push({
          fileName: item.fileName,
          exportFolder: result.data.snapshot?.metadata?.exportFolder,
          snapshot: result.data.snapshot,
        });
      }
      downloadSnapshotFolderArchive(selectedPath, downloaded);
      return true;
    } catch (error) {
      notify(`Could not download folder: ${error.message}`, "error");
      return false;
    }
  }, [baseID, flushSave, notify, snapshots]);

  /**
   * Re-read the open snapshot from disk.
   *
   * The agent pipeline writes into the same shared folder this editor reads, so
   * the file under an open snapshot can change while you are looking at it. The
   * in-memory copy is only as fresh as the last open or save; this pulls it back
   * in line with disk.
   *
   * No caller today: the preview's reload button used to do this on top of
   * rebooting the engine, and no longer does — it is a restart, not a re-read
   * (containerSnapshotPreview.js). Kept as editor API because re-reading is a
   * real operation on a folder something else is writing into, and reselecting
   * the snapshot is the only other way to ask for it.
   *
   * Queued edits are flushed first: they were made against the old content, and
   * re-reading would otherwise drop them silently.
   */
  const reloadSnapshot = useCallback(async (fileName = loaded.fileName) => {
    if (!fileName) return;
    await flushSave();

    // Reloading a row that is not currently selected should make that snapshot
    // the one shown by the editor, just like selecting it from the tree.
    if (fileName !== selectedFileName) {
      setSelectedFileName(fileName);
      localStorage.setItem(SELECTED_SNAPSHOT_STORAGE_KEY, fileName);
    }
    await loadSnapshot(fileName);
  }, [flushSave, loadSnapshot, loaded.fileName, selectedFileName]);

  const selectSnapshot = useCallback(
    async (fileName) => {
      if (!fileName || fileName === selectedFileName) return;
      // Don't leave a debounced edit behind on the file being navigated away from.
      await flushSave();
      setSelectedFileName(fileName);
      localStorage.setItem(SELECTED_SNAPSHOT_STORAGE_KEY, fileName);
      void loadSnapshot(fileName);
    },
    [flushSave, loadSnapshot, selectedFileName]
  );

  // Initial load: list the folder, then restore the last selection when it is
  // still there (the agent may have discarded it since), else take the first.
  useEffect(() => {
    if (!baseID) return undefined;
    let cancelled = false;

    (async () => {
      const list = await refreshSnapshots();
      if (cancelled || list.length === 0) return;

      const remembered = localStorage.getItem(SELECTED_SNAPSHOT_STORAGE_KEY);
      const readable = list.filter((item) => item.isReadable !== false);
      const restored = readable.find((item) => item.fileName === remembered) || readable[0];
      if (!restored) return;

      setSelectedFileName(restored.fileName);
      localStorage.setItem(SELECTED_SNAPSHOT_STORAGE_KEY, restored.fileName);
      void loadSnapshot(restored.fileName);
    })();

    return () => {
      cancelled = true;
    };
    // Only on base switch — refreshSnapshots/loadSnapshot are stable per baseID.
  }, [baseID]);

  // On unmount the debounce timer would never fire, so send whatever is queued
  // rather than dropping it. Fire-and-forget: React will not await this, but the
  // request is already on the wire by the time the component is gone.
  // Builds and deliveries, held here rather than in the dialogs that start
  // them: they run for minutes in the agent container and must not stop the
  // person who started them from working. See useSnapshotJobs.
  const jobs = useSnapshotJobs({ baseID, createSnackbar, refreshSnapshots });

  // Frozen by `metadata.locked`. Everything that can write keys off this, and
  // the server enforces the same flag — this is the read-only signal the panels
  // and the toolbar share.
  const isLocked = _.get(loaded.snapshot, "metadata.locked", false) === true;

  const flushSaveRef = useRef(flushSave);
  flushSaveRef.current = flushSave;
  useEffect(() => {
    return () => {
      void flushSaveRef.current();
    };
  }, []);

  return {
    applySnapshotChange,
    areSnapshotsLoaded,
    baseID,
    // Whether this base has the delivery half of the pipeline. The server
    // refuses those routes without it, so the UI must not offer them.
    taskDelivery,
    // Whether snapshots here can be saved into another base as a version.
    exportAsVersion,
    // Locked snapshots take no edits, so there is nothing to step through.
    canRedo: historyDepth.redo > 0 && !isLocked,
    canUndo: historyDepth.undo > 0 && !isLocked,
    conflict,
    configRevision,
    contentHash: loaded.contentHash,
    downloadSnapshot,
    downloadFolder,
    flushSave,
    flow,
    isLocked,
    isPortrait,
    isSaving,
    isSnapshotLoading,
    // The long jobs this editor has running, and the way to start one.
    jobs,
    loadSnapshot,
    // The file `snapshot` actually holds — what the preview and the settings
    // panels key off. `selectedFileName` is what the tree highlights.
    loadedFileName: loaded.fileName,
    mutateSnapshot,
    redo,
    refreshSnapshots,
    reloadSnapshot,
    selectSnapshot,
    selectedFileName,
    setIsPortrait,
    setSnapshotPathValue,
    snapshot: loaded.snapshot,
    snapshotSizeBytes: loaded.sizeBytes,
    // What the open snapshot would export to, worked out by the server from
    // the engine's own asset rules. Only the open one has it — it is computed
    // on demand, not kept.
    snapshotEstimatedBytes: loaded.estimatedBytes,
    snapshots,
    undo,
  };
}
